/**
 * @ Copyright (c) 2022-present, Highway 9 Networks, Inc.
 * @description cache object to store the response object
 */
const cache = new Map<string, { time: number; response: Response }>();
const cacheTime = 5 * 1000;
const noCaching = true;

// call refreshCache function every n secs
if (!noCaching) {
  setInterval(refreshCache, cacheTime);
}

function refreshCache() {
  if (cache.size === 0 || noCaching) {
    return;
  }
  // for each property in the cache object, check if the property is add in cache object before 5secs
  // if yes, remove it from cache object
  cache.forEach((value, key) => {
    if (Date.now() - value.time > cacheTime) {
      cache.delete(key);
    }
  });
}

// check in cache if the url is already present in cache object then resolve the promise
export async function fetchCache(
  url: string,
  opts: RequestInit,
  resolve: (value: Response | PromiseLike<Response>) => void,
  reject: (reason?: any) => void,
  noCaching = false
) {
  if (cache.size === 0 || noCaching) {
    return false;
  }
  try {
    // check if method is GET
    if (opts.method === "GET") {
      // check if url is present in cache object
      if (cache.has(url)) {
        // clone the response object and return it
        const data = cache.get(url)!;
        const res = data.response.clone();
        resolve(data.response);
        // set the response object in cache object
        cache.set(url, {
          ...data,
          response: res,
        });
        // console.count(`cache hit for ${url}`);
        return true;
      }
    }
    // if method is PUT, POST or DELETE then remove the parent url from cache object
    else if (opts.method === "PUT" || opts.method === "POST" || opts.method === "DELETE") {
      let parentUrl: string | string[] = url.split("/");
      if(opts.method !== "POST"){
        // Remove the ID from the URL (the POST api call does not have an ID)
        parentUrl.pop();
      }
      parentUrl = parentUrl.join("/");
      if (cache.has(parentUrl)) {
        cache.delete(parentUrl);
      }
    }
    return false;
  } catch (error) {
    reject(error);
  }
}

// create a cache object
// add the time when the object is created
// add the object to cache object
export function createCache(
  url: string,
  opts: RequestInit,
  response: Response,
  resolve: (value: Response | PromiseLike<Response>) => void,
  reject: (reason?: any) => void
) {
  try {
    if (noCaching) return resolve(response);
    // console.log("cache miss", cache);
    if (opts.method === "GET") {
      const resClone = response.clone();
      cache.set(url, {
        time: Date.now(),
        response: resClone,
      });
    }
    return resolve(response);
  } catch (error) {
    reject(error);
  }
}
